import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import Loader from "_shared/_views/loading/loader";
import { CanceledError } from "axios";
import { enqueueSnackbar } from "notistack";
import React, { useEffect } from "react";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency, Masked, MaskedFeeSchedule } from "work/_shared/masked-fields";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import {
  FeeScheduleTemplateOrderField,
  FeeScheduleTemplateOrderParameters
} from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-parameters";
import { FeeScheduleTemplateFilterParameters } from "work/fee-schedule-templates/_api/queries/contracts/filtering/fee-schedule-template-filter-parameters";
import FeeScheduleTemplateQueryAPIService from "work/fee-schedule-templates/_api/queries/fee-schedule-template-query-api-service";
import FeeScheduleCategory from "../fee-schedule-category";

const ListContainer = styled(List)(({ theme }) => ({
  maxHeight: theme.spacing(50),
  maxWidth: theme.spacing(50),
}));

type FeeScheduleCategoryListProps = {
  categories?: Array<FeeScheduleCategory> | Array<FeeScheduleCategoryMaskedCurrency>;
  disabledCategories?: Array<FeeScheduleCategory> | Array<FeeScheduleCategoryMaskedCurrency>;
  onCategoriesSelected: (categories: FeeScheduleCategory[]) => void;
};

export default function FeeScheduleCategoryList(props: Readonly<FeeScheduleCategoryListProps>) {
  const { categories, disabledCategories, onCategoriesSelected } = props;

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [existingCategories, setExistingCategories] = React.useState<FeeScheduleCategory[]>([]);
  const [existingMaskedCategories, setExistingMaskedCategories] = React.useState<FeeScheduleCategoryMaskedCurrency[]>(
    []
  );

  const [selectedCategories, setSelectedCategories] = React.useState<FeeScheduleCategory[]>([]);
  const [paginatedParameters, setPaginatedParameters] = React.useState<PaginationParameters>();

  const session = useSession();

  useEffect(() => {
    let abortController = new AbortController();
    if (!categories) return;

    setExistingCategories(
      categories.filter(
        (cat) => cat instanceof FeeScheduleCategory && !(cat instanceof FeeScheduleCategoryMaskedCurrency)
      ) as FeeScheduleCategory[]
    );
    setExistingMaskedCategories(categories.filter((cat) => cat instanceof FeeScheduleCategoryMaskedCurrency));

    if (categories?.every((cat) => MaskedFeeSchedule.isMasked(cat))) {
      setExistingMaskedCategories(categories as FeeScheduleCategoryMaskedCurrency[]);
    } else if (categories) {
      setExistingCategories(categories as FeeScheduleCategory[]);
    } else {
      void getExistingCategories(abortController);
      return () => {
        abortController.abort();
        abortController = new AbortController();
      };
    }
  }, []);

  async function getExistingCategories(abortController: AbortController) {
    const entityId = session.user!.legalEntityId;
    if (!entityId) return;

    setIsLoading(true);
    try {
      const filterParameters = new FeeScheduleTemplateFilterParameters({
        entityId
      });

      const orderParameters = new FeeScheduleTemplateOrderParameters({
        orderBy: FeeScheduleTemplateOrderField.Name,
        orderDir: OrderDirection.Descending
      });

      const templateQuery: FeeScheduleTemplateQuery = new FeeScheduleTemplateQuery({
        filterParameters,
        orderParameters,
        paginationParameters: paginatedParameters ?? PaginationParameters.create({ pageIndex: 0, pageSize: 100 })
      });

      const feeScheduleService = new FeeScheduleTemplateQueryAPIService(session);
      const templates = await feeScheduleService.getFeeScheduleTemplatesByQuery(templateQuery, abortController);
      templates.data.forEach((template) => {
        template.categories?.forEach((category) => {
          if (Masked.isMasked(category)) {
            existingMaskedCategories.push(FeeScheduleCategoryMaskedCurrency.fromJSON(category));
          } else {
            existingCategories.push(FeeScheduleCategory.fromJSON(category) as FeeScheduleCategory);
          }
        });
      });
    } catch (error) {
      if (error instanceof CanceledError) return;
      console.error(error);
      enqueueSnackbar("Failed to load existing fee schedule templates.", {
        variant: "error",
      });
    } finally {
      setPaginatedParameters((prev) => {
        if (prev) {
          return PaginationParameters.create({ pageIndex: prev.pageIndex, pageSize: prev.pageSize });
        }
      });
      setIsLoading(false);
    }
  }

  const isFormValid = () => selectedCategories?.length > 0;

  function handleSelectClicked(category: FeeScheduleCategory) {
    const selected = [...selectedCategories];
    const index = selected.indexOf(category);
    if (index > -1) {
      selected.splice(index, 1);
    } else {
      selected.push(category);
    }
    setSelectedCategories(selected);
  }

  function isSelected(category: FeeScheduleCategory) {
    return selectedCategories?.map((c) => c.name).includes(category.name);
  }

  function handleAddSelectedClicked() {
    onCategoriesSelected?.(selectedCategories);
  }

  return (
    <div>
      <ListContainer>
        {isLoading && <Loader />}
        {!isLoading && (
          <>
            {(!existingCategories || existingCategories.length < 1) && (
              <Typography>No existing fee schedule categories</Typography>
            )}
            {existingCategories.map((category) => {
              return (
                <ListItemButton
                  key={category.name.value}
                  alignItems="flex-start"
                  disableGutters
                  disabled={disabledCategories?.map((c) => c.name?.value).includes(category.name.value)}
                  onClick={() => handleSelectClicked(category)}
                >
                  <ListItemIcon>
                    <Checkbox
                      color="primary"
                      checked={
                        isSelected(category) ||
                        disabledCategories?.map((c) => c.name.value).includes(category.name.value)
                      }
                      value={category}
                    />
                  </ListItemIcon>
                  <ListItemText
                    style={{ whiteSpace: "pre-line" }}
                    primary={category.name.value}
                    secondary={category.feeString}
                  />
                </ListItemButton>
              );
            })}
          </>
        )}
      </ListContainer>
      <Button color="primary" variant="contained" disabled={!isFormValid()} onClick={handleAddSelectedClicked}>
        Add Selected
      </Button>
    </div>
  );
}
